// Origin sanity check after the July 2026 cross-deploy incident.
//
// Between July 15–16, 2026 a foreign PWA (IMS) was deployed over this origin
// and registered its own service worker at /sw.js. Because our SW lives at the
// same URL and calls skipWaiting()/clientsClaim(), it takes back control on a
// visitor's next online navigation — but the foreign app may have left caches
// behind, and defensively we also want to shed any unexpected SW registration.
//
// This module runs only from OUR bundle (so only once our HTML is being
// served again) and:
//   1. deletes any Cache Storage cache we don't recognize, and
//   2. unregisters any service worker that isn't ours at /sw.js.
//
// Safe to keep long-term: on a healthy origin it finds nothing to do.

const OWN_SW_PATH = '/sw.js';

// Caches we create: "receiptraptor-precache-*" (workbox cacheId, vite.config.ts)
// plus the two runtimeCaching cache names.
function isOwnCache(name: string): boolean {
  return name.startsWith('receiptraptor-') || name === 'api-cache' || name === 'static-assets';
}

export async function cleanupForeignServiceWorkerState(): Promise<void> {
  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(
        names.filter((name) => !isOwnCache(name)).map((name) => caches.delete(name))
      );
    }

    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations
          .filter((reg) => {
            const worker = reg.active ?? reg.waiting ?? reg.installing;
            if (!worker) return false;
            const url = new URL(worker.scriptURL);
            return url.origin !== window.location.origin || url.pathname !== OWN_SW_PATH;
          })
          .map((reg) => reg.unregister())
      );
    }
  } catch {
    // Cleanup is best-effort; never block app startup on it.
  }
}
